import React, { useState } from 'react';
import styled from 'styled-components';
import { useTransitionNavigate } from './PageTransition';
import lang from '../language/Eng_eng.json';

// ─── Nav Wrapper ──────────────────────────────────────────────────────────────

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: #F3EFE6;
  border-bottom: 1px solid #BCA18D;
  box-sizing: border-box;
`;

/** Top horizontal bar — same on desktop & mobile */
const NavMainRow = styled.div`
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 5%;
  position: relative;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: 100px;
    padding: 0 6%;
  }
`;

/** Mobile-only bottom row for RESERVATION */
const NavBottomRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 13px 6% 17px;
    border-top: 1px solid rgba(188, 161, 141, 0.4);
  }
`;

// ─── Left Section ─────────────────────────────────────────────────────────────

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const MenuIcon = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;

  span {
    display: block;
    width: 28px;
    height: 2px;
    background-color: #5C4033;
    transition: background-color 0.3s ease;
  }
`;

/** Desktop only: chef hats in left section */
const ChefIconsDesktop = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ChefIcon = styled.img`
  height: 62px;
  object-fit: contain;
`;

// ─── Center Section ───────────────────────────────────────────────────────────

const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;

  @media (max-width: 768px) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    flex: none;
    pointer-events: none;
  }
`;

const LogoImage = styled.img`
  height: 68px;
  object-fit: contain;
  cursor: pointer;
  pointer-events: all;

  @media (max-width: 768px) {
    height: 64px;
  }
`;

// ─── Right Section (Desktop) ──────────────────────────────────────────────────

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  flex: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

/** Mobile-only chef hats */
const MobileRightSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileChefIcons = styled.div`
  display: flex;
  gap: 5px;
`;

const MobileChefIcon = styled.img`
  height: 30px;
  object-fit: contain;
  opacity: 0.9;
`;

// ─── Shared Icons / Buttons ───────────────────────────────────────────────────

const ReservationButton = styled.a`
  padding: 10px 25px;
  border: 1px solid #5C4033;
  color: #5C4033;
  font-family: 'EB Garamond', serif;
  font-size: 0.8rem;
  letter-spacing: 0.15rem;
  text-transform: uppercase;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #5C4033;
    color: #F3EFE6;
  }
`;

/** Wider mobile version of the reservation button */
const MobileReservationButton = styled(ReservationButton)`
  width: 65%;
  text-align: center;
  padding: 13px 0;
  font-size: 0.8rem;
  letter-spacing: 0.2rem;
`;

// ─── Drawer Components ────────────────────────────────────────────────────────

const DrawerOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 2000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const Drawer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 350px;
  height: 100%;
  background: #ffffff;
  z-index: 2001;
  transform: translateX(${props => props.isOpen ? '0' : '-100%'});
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  box-shadow: 5px 0 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 80%;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 60px;
`;

const DrawerLogo = styled.img`
  height: 60px;
  object-fit: contain;
`;

const CloseButton = styled.div`
  cursor: pointer;
  color: #5C4033;
  font-size: 2rem;
  line-height: 1;
`;

const DrawerNav = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -40px;
`;

const DrawerNavLink = styled.button`
  font-family: 'EB Garamond', serif;
  font-size: 1.5rem;
  text-transform: uppercase;
  color: #5C4033;
  letter-spacing: 0.1rem;
  text-decoration: none;
  background: none;
  border: none;
  padding: 18px 40px;
  cursor: pointer;
  text-align: left;
  position: relative;
  overflow: hidden;
  transition: color 0.35s ease;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: #BCA18D;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 0;
  }

  span {
    position: relative;
    z-index: 1;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    color: #ffffff;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const RESERVATION_URL =
  'https://www.opentable.com.au/booking/restref/availability?lang=en-AU&correlationId=2137dfd1-2106-4942-91d0-1229cda41100&restRef=263042&otSource=Restaurant%20website';

const Top_Menu: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { navigateTo } = useTransitionNavigate();

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  const handleNav = (path: string) => {
    setIsMenuOpen(false);
    navigateTo(path);
  };

  return (
    <>
      <Nav>
        {/* ── Main row ── */}
        <NavMainRow>
          {/* Left: hamburger + chef hats (desktop) */}
          <LeftSection>
            <MenuIcon onClick={toggleMenu}>
              <span /><span /><span />
            </MenuIcon>
            <ChefIconsDesktop>
              <ChefIcon src="/logo/Hats.png" alt="Chef Hats" />
            </ChefIconsDesktop>
          </LeftSection>

          {/* Center: logo (absolutely centered on mobile) */}
          <CenterSection>
            <LogoImage
              src="/logo/logo.png"
              alt="Allta Logo"
              onClick={() => handleNav('/')}
            />
          </CenterSection>

          {/* Right: reservation (desktop only) */}
          <RightSection>
            <ReservationButton href={RESERVATION_URL} target="_blank" rel="noopener noreferrer">
              {lang.common.bookNow}
            </ReservationButton>
          </RightSection>

          {/* Mobile only: chef hats */}
          <MobileRightSection>
            <MobileChefIcons>
              <MobileChefIcon src="/logo/Hats.png" alt="Chef Hats" />
            </MobileChefIcons>
          </MobileRightSection>
        </NavMainRow>

        {/* ── Mobile bottom row: RESERVATION button ── */}
        <NavBottomRow>
          <MobileReservationButton href={RESERVATION_URL} target="_blank" rel="noopener noreferrer">
            {lang.common.bookNow}
          </MobileReservationButton>
        </NavBottomRow>
      </Nav>

      {/* Drawer */}
      <DrawerOverlay isOpen={isMenuOpen} onClick={toggleMenu} />
      <Drawer isOpen={isMenuOpen}>
        <DrawerHeader>
          <DrawerLogo src="/logo/logo.png" alt="Allta Logo" />
          <CloseButton onClick={toggleMenu}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.5"
                 strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </DrawerHeader>
        <DrawerNav>
          <DrawerNavLink onClick={() => handleNav('/')}><span>{lang.nav.home}</span></DrawerNavLink>
          <DrawerNavLink onClick={() => handleNav('/about')}><span>{lang.nav.about}</span></DrawerNavLink>
          <DrawerNavLink onClick={() => handleNav('/menu')}><span>{lang.nav.menu}</span></DrawerNavLink>
          <DrawerNavLink onClick={() => handleNav('/news')}><span>{lang.nav.news}</span></DrawerNavLink>
          <DrawerNavLink onClick={() => handleNav('/contact')}><span>{lang.nav.contact}</span></DrawerNavLink>
          <DrawerNavLink
            as="a"
            href={RESERVATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={toggleMenu}
            style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '28px' }}
          >
            <span>{lang.common.bookNow}</span>
          </DrawerNavLink>
        </DrawerNav>
      </Drawer>
    </>
  );
};

export default Top_Menu;
